import React, { useCallback, useEffect, useState } from 'react';

import AdminDataTable from '../../components/admin/AdminDataTable';
import { formatAdminDate } from '../../components/admin/adminFormatting';
import SubscriptionChangeRequestsPanel from '../../components/admin/SubscriptionChangeRequestsPanel';
import ActionsMenu from '../../components/common/ActionsMenu';
import Badge from '../../components/common/Badge';
import { Button, Input, PageHeader, Select } from '@copilot/ui';
import { Field } from '../../components/common/Field';
import { Modal } from '@copilot/ui';
import * as adminApi from '../../services/adminApi';
import { formatPlanPrice } from '../../services/pricingApi';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR'];

// A blank plan, used both for "New Plan" and as the shape every form
// field reads from - so the form never has to cope with an undefined
// value on first render. Monthly is offered by default (the common
// case); yearly is opt-in.
//
// A plan's ONLY usage limit is Meetings / month. Meeting minutes,
// meeting length and AI-request counts are not plan limits, so they are
// not form fields - see LEGACY_NON_ENFORCING below.
const EMPTY_PLAN = {
  name: '',
  description: '',
  currency: 'INR',
  offerMonthly: true,
  monthly_price: '0',
  offerYearly: false,
  yearly_price: '0',
  unlimitedMeetings: false,
  max_meetings: '10',
  features: '',
  is_active: true,
  is_featured: false,
  // Blank on purpose - see the Plan level field below. A real plan must
  // be given a level deliberately; defaulting it would put a new plan
  // level with Free and make every change to it "lateral".
  tier_rank: '',
};

// Features are one-per-line in the textarea and a JSON array on the
// wire; these two helpers are the single conversion point.
function featuresToText(features) {
  return (features || []).join('\n');
}

function textToFeatures(text) {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function planToForm(plan) {
  return {
    name: plan.name,
    description: plan.description || '',
    currency: plan.currency,
    offerMonthly: plan.monthly_price != null,
    monthly_price: plan.monthly_price != null ? String(plan.monthly_price) : '0',
    offerYearly: plan.yearly_price != null,
    yearly_price: plan.yearly_price != null ? String(plan.yearly_price) : '0',
    unlimitedMeetings: plan.max_meetings == null,
    max_meetings: plan.max_meetings != null ? String(plan.max_meetings) : '10',
    features: featuresToText(plan.features),
    is_active: plan.is_active,
    is_featured: plan.is_featured,
    tier_rank: String(plan.tier_rank ?? 0),
  };
}

// The extra-meeting rate is not entered: the backend derives it as the
// monthly price divided by the included meetings, rounded half-up to the
// paisa (backend/services/billing/extra_meeting_pricing.py). This is a
// PREVIEW of that rule while the form is being edited - it is never sent,
// and checkout always charges the server's own figure. Done in whole
// paise (integers), never in floating-point rupees.
const MIN_RATE_PAISE = 100; // Rs 1 - the provider's minimum order
const MAX_RATE_PAISE = 10000000; // Rs 1,00,000

function rupeesToPaise(text) {
  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(String(text ?? '').trim());
  if (!match) return null;
  return Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
}

// Rate in paise, or null when the plan cannot sell extra meetings.
export function previewExtraMeetingRatePaise(form) {
  if (form.currency !== 'INR' || !form.offerMonthly || form.unlimitedMeetings) return null;
  const monthly = rupeesToPaise(form.monthly_price);
  if (monthly == null || monthly <= 0) return null;
  if (form.offerYearly && rupeesToPaise(form.yearly_price) === 0) return null; // free plan
  const meetings = Number(form.max_meetings);
  if (!Number.isInteger(meetings) || meetings <= 0) return null;
  // round(monthly / meetings) with halves rounded up, in integers.
  const rate = Math.floor((2 * monthly + meetings) / (2 * meetings));
  return rate >= MIN_RATE_PAISE && rate <= MAX_RATE_PAISE ? rate : null;
}

export function formatRatePaise(paise) {
  if (paise == null) return null;
  return `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// The saved plan's rate, as the backend calculated it.
function formatSavedRate(price) {
  if (price == null) return null;
  return `₹${Number(price).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// The plan level must be a whole number >= 0. Blank is NOT allowed: the
// backend requires it, and it decides what counts as a downgrade.
function isValidTierRank(value) {
  if (value === '' || value == null) return false;
  const number = Number(value);
  return Number.isInteger(number) && number >= 0;
}

// What every saved plan carries for the fields that are no longer plan
// limits (backend/models/pricing_plan.py): the legacy minute/duration
// columns at 0 (non-enforcing), unlimited AI requests, and every
// capability on. Sent on every save so an older plan edited here is
// brought onto the meeting-count-only model.
const LEGACY_NON_ENFORCING = {
  monthly_minutes_limit: 0,
  max_meeting_duration_minutes: 0,
  monthly_ai_requests_limit: null,
  allows_ask_copilot: true,
  allows_document_context: true,
  allows_meeting_analysis: true,
};

function formToPayload(form) {
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    currency: form.currency,
    monthly_price: form.offerMonthly ? Number(form.monthly_price) || 0 : null,
    yearly_price: form.offerYearly ? Number(form.yearly_price) || 0 : null,
    ...LEGACY_NON_ENFORCING,
    max_meetings: form.unlimitedMeetings ? null : Number(form.max_meetings) || 0,
    features: textToFeatures(form.features),
    is_active: form.is_active,
    is_featured: form.is_featured,
    tier_rank: Number(form.tier_rank),
    // No extra-meeting price: the backend derives it from the monthly
    // price and the included meetings.
  };
}

function PlanFormModal({ initialPlan, isSaving, error, onSubmit, onClose }) {
  const [form, setForm] = useState(
    initialPlan ? planToForm(initialPlan) : EMPTY_PLAN
  );

  const setField = (field) => (event) => {
    const value =
      event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  // A plan must offer at least one cycle (backend/models/pricing_plan.py's
  // ck_pricing_plans_has_price) - checked here too so Save is disabled
  // before the request round-trips just to say the same thing.
  const noCycleSelected = !form.offerMonthly && !form.offerYearly;
  const tierRankInvalid = !isValidTierRank(form.tier_rank);
  const autoExtraRate = formatRatePaise(previewExtraMeetingRatePaise(form));

  return (
    <Modal
      title={initialPlan ? `Edit ${initialPlan.name}` : 'New Pricing Plan'}
      onClose={onClose}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(formToPayload(form))}
            disabled={isSaving || noCycleSelected || tierRankInvalid}
          >
            {isSaving ? 'Saving...' : 'Save Plan'}
          </Button>
        </>
      }
    >
      <div className="form-grid admin-plan-form">
        <Field label="Plan Name" htmlFor="plan-name">
          <Input id="plan-name" value={form.name} onChange={setField('name')} required />
        </Field>

        <Field label="Description" htmlFor="plan-description">
          <Input
            id="plan-description"
            value={form.description}
            onChange={setField('description')}
            placeholder="Who this plan is for"
          />
        </Field>

        <Field label="Currency" htmlFor="plan-currency">
          <Select id="plan-currency" value={form.currency} onChange={setField('currency')}>
            {CURRENCIES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Plan level" htmlFor="plan-tier-rank">
          <Input
            id="plan-tier-rank"
            type="number"
            min="0"
            step="1"
            value={form.tier_rank}
            onChange={setField('tier_rank')}
            placeholder="e.g. 100"
            required
          />
        </Field>
        <p className="field-help">
          Higher value means a higher plan. Example: Free 0, Starter 100, Pro
          200. Customers can upgrade to a higher level at any time but cannot
          move to a lower level until their current paid period ends. Changing
          a plan&apos;s price never changes its level.
        </p>
        {tierRankInvalid && (
          <p className="form-alert">
            Enter the plan level as a whole number, 0 or higher.
          </p>
        )}

        <div className="admin-plan-cycle-row">
          <div className="admin-plan-cycle-field">
            <label className="admin-plan-toggle" htmlFor="plan-offer-monthly">
              <input
                id="plan-offer-monthly"
                type="checkbox"
                checked={form.offerMonthly}
                onChange={setField('offerMonthly')}
              />
              Monthly billing
            </label>
            <Field label="Monthly Price" htmlFor="plan-monthly-price">
              <Input
                id="plan-monthly-price"
                type="number"
                min="0"
                step="0.01"
                value={form.monthly_price}
                onChange={setField('monthly_price')}
                disabled={!form.offerMonthly}
              />
            </Field>
          </div>

          <div className="admin-plan-cycle-field">
            <label className="admin-plan-toggle" htmlFor="plan-offer-yearly">
              <input
                id="plan-offer-yearly"
                type="checkbox"
                checked={form.offerYearly}
                onChange={setField('offerYearly')}
              />
              Yearly billing
            </label>
            <Field label="Yearly Price" htmlFor="plan-yearly-price">
              <Input
                id="plan-yearly-price"
                type="number"
                min="0"
                step="0.01"
                value={form.yearly_price}
                onChange={setField('yearly_price')}
                disabled={!form.offerYearly}
              />
            </Field>
          </div>
        </div>

        {noCycleSelected && (
          <p className="form-alert">
            Select at least one billing cycle (monthly, yearly, or both).
          </p>
        )}

        <h3 className="admin-plan-section-title">Meeting Allowance</h3>

        <Field label="Meetings / month" htmlFor="plan-max-meetings">
          <Input
            id="plan-max-meetings"
            type="number"
            min="0"
            value={form.max_meetings}
            onChange={setField('max_meetings')}
            disabled={form.unlimitedMeetings}
            placeholder="e.g. 10"
          />
        </Field>

        <label className="admin-plan-toggle" htmlFor="plan-unlimited-meetings">
          <input
            id="plan-unlimited-meetings"
            type="checkbox"
            checked={form.unlimitedMeetings}
            onChange={setField('unlimitedMeetings')}
          />
          Unlimited meetings
        </label>

        <p className="field-help">
          The only limit a plan has: how many meetings a user may start per
          billing period. Live, browser, desktop and Auto Attend meetings all
          count against it. Every plan includes every feature, with no limit
          on meeting length, Ask KrishvaQ or documents. A changed number
          applies to each subscriber from their next renewal.
        </p>

        <Field label="Automatic Extra Meeting Price" htmlFor="plan-extra-meeting-auto-price">
          <output
            id="plan-extra-meeting-auto-price"
            className="input admin-plan-auto-price"
            aria-live="polite"
          >
            {autoExtraRate ?? 'Not available'}
          </output>
        </Field>
        <p className="field-help">
          Calculated automatically: monthly price ÷ meetings per month,
          rounded to the paisa (₹549 ÷ 5 = ₹109.80). A subscriber pays this
          for each extra meeting after using every included meeting in their
          monthly billing period, and extra meetings expire when that period
          ends. Not available on free, yearly-only, unlimited or non-INR
          plans. A changed price or limit applies to new purchases, never to
          payments already started.
        </p>

        <Field label="Features (one per line)" htmlFor="plan-features">
          <textarea
            id="plan-features"
            className="input admin-plan-features"
            rows={5}
            value={form.features}
            onChange={setField('features')}
            placeholder={'Real-time transcription\nLive translation'}
          />
        </Field>

        <label className="admin-plan-toggle" htmlFor="plan-active">
          <input
            id="plan-active"
            type="checkbox"
            checked={form.is_active}
            onChange={setField('is_active')}
          />
          Active (visible on the public pricing page)
        </label>

        <label className="admin-plan-toggle" htmlFor="plan-featured">
          <input
            id="plan-featured"
            type="checkbox"
            checked={form.is_featured}
            onChange={setField('is_featured')}
          />
          Featured (only one plan can be featured at a time)
        </label>

        {error && <div className="form-alert">{error}</div>}
      </div>
    </Modal>
  );
}

function AdminPricingPage() {
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  const [editingPlan, setEditingPlan] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [planToDelete, setPlanToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  // Trashed plans are hidden until the admin asks for them, matching
  // the backend's include_deleted default.
  const [showDeleted, setShowDeleted] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      setPlans(
        await adminApi.getAdminPricingPlans({
          include_deleted: showDeleted || undefined,
        })
      );
    } catch (err) {
      setError(
        err.status === 403
          ? 'You do not have permission to manage pricing.'
          : err.message || 'Unable to load pricing plans.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [showDeleted]);

  // `load` is rebuilt whenever showDeleted changes, so this refetches
  // on toggle without a second effect.
  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingPlan(null);
    setFormError('');
    setIsFormOpen(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleSubmit = async (payload) => {
    setIsSaving(true);
    setFormError('');

    try {
      if (editingPlan) {
        await adminApi.updateAdminPricingPlan(editingPlan.id, payload);
      } else {
        await adminApi.createAdminPricingPlan(payload);
      }

      setIsFormOpen(false);
      setEditingPlan(null);
      await load();
    } catch (err) {
      setFormError(err.message || 'Unable to save this plan.');
    } finally {
      setIsSaving(false);
    }
  };

  // Inline toggles send only the one field they change - the backend's
  // PUT is a partial update (exclude_unset), so this can never rewrite
  // the plan's price or features as a side effect.
  const patchPlan = async (plan, changes) => {
    setActionError('');

    try {
      await adminApi.updateAdminPricingPlan(plan.id, changes);
      await load();
    } catch (err) {
      setActionError(err.message || 'Unable to update this plan.');
    }
  };

  const closeDeleteModal = () => {
    setPlanToDelete(null);
    setDeleteReason('');
  };

  // Soft delete. The plan row survives, so this succeeds even for a
  // plan that already has subscribers - there is no PLAN_IN_USE
  // refusal to handle any more.
  const handleDelete = async () => {
    setActionError('');
    setIsDeleting(true);

    try {
      await adminApi.deleteAdminPricingPlan(planToDelete.id, deleteReason.trim());
      closeDeleteModal();
      await load();
    } catch (err) {
      setActionError(err.message || 'Unable to move this plan to trash.');
      closeDeleteModal();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestore = async (plan) => {
    setActionError('');

    try {
      await adminApi.restoreAdminPricingPlan(plan.id);
      await load();
    } catch (err) {
      setActionError(err.message || 'Unable to restore this plan.');
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Plan',
      render: (plan) => (
        <button
          type="button"
          className="admin-table-name"
          onClick={() => !plan.deleted_at && openEdit(plan)}
          disabled={Boolean(plan.deleted_at)}
        >
          {plan.name}
          {plan.is_featured && <span className="admin-badge">Featured</span>}
        </button>
      ),
    },
    {
      key: 'price',
      label: 'Price',
      render: (plan) => (
        <>
          {plan.monthly_price != null && (
            <div>
              {formatPlanPrice(plan.monthly_price, plan.currency)}{' '}
              <span className="admin-table-subtext">/ month</span>
            </div>
          )}
          {plan.yearly_price != null && (
            <div>
              {formatPlanPrice(plan.yearly_price, plan.currency)}{' '}
              <span className="admin-table-subtext">/ year</span>
            </div>
          )}
        </>
      ),
    },
    {
      key: 'tier_rank',
      label: 'Level',
      render: (plan) => plan.tier_rank,
    },
    {
      key: 'max_meetings',
      label: 'Meetings',
      render: (plan) =>
        plan.max_meetings == null
          ? 'Unlimited'
          : `${plan.max_meetings.toLocaleString()} / month`,
    },
    {
      key: 'extra_meeting_auto_price',
      label: 'Extra Meeting',
      // The backend's own figure for the saved plan (automatic: monthly
      // price / included meetings), so this table and checkout agree.
      render: (plan) =>
        plan.extra_meeting_auto_price != null ? (
          <>
            {formatSavedRate(plan.extra_meeting_auto_price)}{' '}
            <span className="admin-table-subtext">/ meeting</span>
          </>
        ) : (
          <span className="admin-table-subtext">Not available</span>
        ),
    },
    {
      key: 'is_active',
      label: 'Status',
      // Deleted wins over Active/Disabled: is_active is preserved
      // through a deletion (so a restore puts it back), but what an
      // admin needs to see first about a trashed plan is that it is in
      // the trash.
      render: (plan) =>
        plan.deleted_at ? (
          <Badge variant="disconnected">Deleted</Badge>
        ) : (
          <Badge variant={plan.is_active ? 'listening' : 'neutral'}>
            {plan.is_active ? 'Active' : 'Disabled'}
          </Badge>
        ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (plan) => formatAdminDate(plan.created_at),
    },
    {
      key: 'actions',
      label: 'Actions',
      // A trashed plan gets exactly one action - Restore. Editing,
      // enabling or featuring a plan that is in the trash would put the
      // catalogue in a state the admin cannot see the result of.
      render: (plan) => (
        <ActionsMenu
          items={
            plan.deleted_at
              ? [{ label: 'Restore', onClick: () => handleRestore(plan) }]
              : [
                  { label: 'Edit', onClick: () => openEdit(plan) },
                  {
                    label: plan.is_active ? 'Disable' : 'Enable',
                    onClick: () => patchPlan(plan, { is_active: !plan.is_active }),
                  },
                  {
                    label: 'Feature',
                    onClick: () => patchPlan(plan, { is_featured: true }),
                    disabled: plan.is_featured,
                  },
                  {
                    label: 'Move to Trash',
                    onClick: () => setPlanToDelete(plan),
                    variant: 'danger',
                  },
                ]
          }
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Pricing"
        description="Manage the plans users can subscribe to. Plans differ only by meetings per month; every feature is included on every plan."
        action={<Button onClick={openCreate}>New Plan</Button>}
      />

      <SubscriptionChangeRequestsPanel />

      <div className="admin-toolbar">
        <label className="admin-plan-toggle" htmlFor="pricing-show-deleted">
          <input
            id="pricing-show-deleted"
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
          />
          Show plans in trash
        </label>
      </div>

      {actionError && <div className="form-alert">{actionError}</div>}

      {error ? (
        <div className="form-alert">{error}</div>
      ) : (
        <AdminDataTable
          columns={columns}
          rows={plans}
          getRowKey={(plan) => plan.id}
          isLoading={isLoading}
          loadingLabel="Loading pricing plans..."
          emptyTitle="No pricing plans yet."
          emptyDescription="Create your first plan to start limiting meetings per month."
        />
      )}

      {isFormOpen && (
        <PlanFormModal
          initialPlan={editingPlan}
          isSaving={isSaving}
          error={formError}
          onSubmit={handleSubmit}
          onClose={() => {
            setIsFormOpen(false);
            setEditingPlan(null);
          }}
        />
      )}

      {planToDelete && (
        <Modal
          title={`Move ${planToDelete.name} to trash?`}
          onClose={() => !isDeleting && closeDeleteModal()}
          actions={
            <>
              <Button
                variant="secondary"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? 'Working...' : 'Move to Trash'}
              </Button>
            </>
          }
        >
          <p>Are you sure you want to move this item to trash?</p>
          <p className="field-help">
            The plan disappears from the public pricing page and stops
            accepting new subscribers. Nothing is erased: anyone already
            subscribed keeps their plan until the end of their current
            billing period, and you can restore it at any time.
          </p>

          <Field label="Reason (optional)" htmlFor="plan-delete-reason">
            <Input
              id="plan-delete-reason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Why is this plan being retired?"
              maxLength={500}
            />
          </Field>
        </Modal>
      )}
    </div>
  );
}

export default AdminPricingPage;
